import { Component, ViewChild } from '@angular/core';
import { FeedbackService } from '../../../service/feedback.service';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { FeedbackCommentTimelineComponent } from '../feedback-comment-timeline/feedback-comment-timeline.component';
import {
  ActivityLog,
  Feedback,
  FeedbackStatus,
  ConversationDetailsResponse,
  FeedbackStatusResponse,
  CommentResponse
} from '../../../utility/feedback';
import { AbioMedChatComponent } from '../../abiomed-chat/abiomed-chat.component';
import { FeedbackConversationTabsComponent } from '../feedback-conversation-tabs/feedback-conversation-tabs.component';
import { InteractionService } from '../../../service/interaction/interaction.service';
import { AssignTicketToDialogComponent } from '../../ContactCenter/assign-ticket-to-dialog/assign-ticket-to-dialog.component';


@Component({
    selector: 'app-comment-history',
    templateUrl: './comment-history.component.html',
    styleUrls: ['./comment-history.component.scss'],
    standalone: false
})
export class CommentHistoryComponent {
  @ViewChild('timelineRef') timelineRef!: FeedbackCommentTimelineComponent;
  historyDetails: ActivityLog[] = [];
  feedback: Feedback = {} as Feedback;
  feedbackStatusList: FeedbackStatus[] = [];
  selectedFeedbackStatus: string = '';
  isLoading: boolean = false;
  private feedbackSubscription: Subscription | null = null;
  filterCtrl: FormControl = new FormControl('');
  commentText: string = '';
  isSubmitting: boolean = false;
  feedbackId: number = 0;
  feedbackDetails: any;
  evaluationIssues: string[] = [];
  uploadedFiles: File[] = [];
  private hasUnassignedBotActivity: boolean = false;

  get isFeedbackClosed(): boolean {
    return this.feedback.feedback_status?.toUpperCase() === 'CLOSED';
  }
  constructor(
    private feedbackService: FeedbackService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private intService: InteractionService
  ) {
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.queryParams && navigation.extras.queryParams['feedbackDetails']) {
      this.feedbackDetails = navigation.extras.queryParams['feedbackDetails'];
    } else if (window.history.state && window.history.state['feedbackDetails']) {
      this.feedbackDetails = window.history.state['feedbackDetails'];
    }
  }


  ngOnInit(): void {
    if (!this.feedbackDetails) {
      this.route.queryParams.subscribe(params => {
        if (params['feedbackDetails']) {
          this.feedbackDetails = params['feedbackDetails'];
        }
      });
    }

    this.conversationDetails();
  }

  conversationDetails(): void {
    this.feedbackService.getConversationDetails(this.feedbackDetails?.conversation_feedback_id).subscribe((data: ConversationDetailsResponse) => {
      this.feedback = data.feedback || {} as Feedback;
      this.feedbackId = this.feedbackDetails?.conversation_feedback_id || this.feedback.id;

      if (this.feedback.created_date) {
        this.feedback.created_date = this.convertUtcToLocal(this.feedback.created_date);
      }

      this.selectedFeedbackStatus = this.feedback.feedback_status || '';
      this.hasUnassignedBotActivity = (data.activity_logs || []).some((log: ActivityLog) =>
        log.user_type?.toLowerCase() === 'bot' &&
        !log.assigned_from_category &&
        !log.assigned_to_category
      );
      this.historyDetails = (data.activity_logs || [])
        .filter((log: ActivityLog) => log.user_type !== 'bot')
        .map((log: ActivityLog) => {
          const created_at = log.created_at ? this.convertUtcToLocal(log.created_at) : log.created_at;
          return {
            ...log,
            created_at: created_at,
            isUser: log.wwid === Number(sessionStorage.getItem('userId')),
            attachment: (log as any).uploaded_file || (log as any).attachment || null,
          };
        });
      this.historyDetails.reverse();
      this.loadEvaluationIssues();
      this.getFeedbackstatusMetadataList();
    });
  }

  viewFeedbackConversation(): void {
    let feedback = this.feedbackDetails;
    const chatSessionId = feedback.chat_session_id || feedback.session_id || feedback.conversation_id;

    if (!chatSessionId) {
      console.error('No chat session ID found for this feedback');
      return;
    }

    sessionStorage.setItem('feedbackId', feedback.conversation_id || feedback.conversation_feedback_id);

    this.intService.loadHistoryDetailsHandler({ chat: 'CHAT', id: chatSessionId });

    const dialogRef = this.dialog.open(FeedbackConversationTabsComponent, {
      width: '95%',
      maxWidth: '1400px',
      height: '90vh',
      data: {
        chatSessionId: chatSessionId,
        feedbackId: feedback.conversation_id || feedback.conversation_feedback_id,
        userRequest: feedback.user_request,
        feedbackDetails: feedback
      },
      panelClass: 'feedback-tabs-dialog'
    });

    dialogRef.afterClosed().subscribe(result => {
      sessionStorage.removeItem('feedbackId');
    });
  }

  loadEvaluationIssues(): void {
    this.feedbackService.botEvaluationTab(this.feedbackId).subscribe((data: any) => {
      const evaluations = Array.isArray(data?.evaluations) ? data.evaluations : (Array.isArray(data?.bot_eval_with_feedback?.evaluations) ? data.bot_eval_with_feedback.evaluations : []);
      if (evaluations && evaluations.length > 0) {
        const evaluation = evaluations[0];
        if (evaluation?.issue_label) {
          const issueLabel = evaluation.issue_label;
          const friendlyLabels = issueLabel.business_friendly_labels || {};
          const issues: string[] = [];
          Object.keys(friendlyLabels).forEach(key => {
            if (friendlyLabels[key]) {
              issues.push(friendlyLabels[key]);
            }
          });
          const uniqueIssues = Array.from(new Set(issues));
          if (uniqueIssues.length > 0) {
            this.evaluationIssues = uniqueIssues;
            this.addAICommentForIssues(uniqueIssues);
          }
        }
      }
    });
  }

  addAICommentForIssues(issues: string[]): void {
    const issuesText = issues.join(', ');
    const aiComment: any = {
      first_name: this.hasUnassignedBotActivity ? 'System' : 'AI',
      last_name: '',
      message: 'Open because of',
      issuesText: issuesText,
      created_at: this.feedback.created_date || '',
      user_type: this.hasUnassignedBotActivity ? 'bot' : 'ai',
      wwid: 0,
      isUser: false,
      isAI: !this.hasUnassignedBotActivity,
      hasIssueLinks: true
    };
    // Insert at index 1 from the bottom (just above the last item)
    const insertIndex = Math.max(this.historyDetails.length - 1, 0);
    this.historyDetails.splice(insertIndex, 0, aiComment);
  }

  openEvaluationDialog(tabIndexOrEvent?: number | { tabIndex: number, evaluationOnly?: boolean }, evaluationOnly = false): void {
    let tabIndex: number | undefined;
    let isEvaluationOnly = evaluationOnly;

    // Handle both old number parameter and new object parameter
    if (typeof tabIndexOrEvent === 'object' && tabIndexOrEvent !== null) {
      tabIndex = tabIndexOrEvent.tabIndex;
      isEvaluationOnly = tabIndexOrEvent.evaluationOnly || false;
    } else if (typeof tabIndexOrEvent === 'number') {
      tabIndex = tabIndexOrEvent;
    } else {
      tabIndex = undefined;
    }

    const feedback = this.feedbackDetails;
    const chatSessionId = feedback.chat_session_id || feedback.session_id || feedback.conversation_id;

    if (!chatSessionId) {
      console.error('No chat session ID found for this feedback');
      return;
    }
    sessionStorage.setItem('feedbackId', feedback.conversation_id || feedback.conversation_feedback_id);

    // Only load chat history if not evaluation-only mode
    if (!isEvaluationOnly) {
      this.intService.loadHistoryDetailsHandler({ chat: 'CHAT', id: chatSessionId });
    }

    const dialogRef = this.dialog.open(FeedbackConversationTabsComponent, {
      width: '95%',
      maxWidth: '1400px',
      height: '90vh',
      data: {
        feedbackId: this.feedbackId,
        userRequest: feedback.user_request,
        feedbackDetails: feedback,
        chatSessionId: chatSessionId,
        selectedTabIndex: isEvaluationOnly ? 0 : 1,
        targetEvaluationTabLabel: 'Issues',
        evaluationOnly: isEvaluationOnly
      },
      panelClass: 'feedback-tabs-dialog'
    });

    dialogRef.afterClosed().subscribe(result => {
      sessionStorage.removeItem('feedbackId');
    });
  }

  openEvaluationOnlyDialog(tabIndex?: number): void {
    this.openEvaluationDialog(tabIndex, true);
  }

  getFeedbackstatusMetadataList(): void {
    this.isLoading = true;
    this.feedbackSubscription = this.feedbackService.getFeedbackstatusMetadataList().subscribe({
      next: (response: any[]) => {
        if (response && response.length > 0) {
          this.feedbackStatusList = response;
        } else {
          this.feedbackStatusList = [];
          this.toastr.warning('No feedback statuses available.');
        }
        this.isLoading = false;
      },
      error: (error: Error) => {
        console.error('Error fetching feedback status metadata:', error);
        this.toastr.error('Failed to load feedback statuses.');
        this.feedbackStatusList = [];
        this.isLoading = false;
      }
    });
  }

  onFeedbackStatusChange(selectedValue: string): void {
    this.selectedFeedbackStatus = selectedValue;
  }

  onFilesChanged(files: File[]): void {
    this.uploadedFiles = files;
  }



  convertUtcToLocal(utcDateString: string): string {
    if (!utcDateString) return utcDateString;
    const utcDate = utcDateString.includes('Z') ? utcDateString : utcDateString.split('.')[0] + 'Z';
    return utcDate;
  }

  addComment(): void {
    if (!this.selectedFeedbackStatus) {
      this.toastr.error('Please select a feedback status.');
      return;
    }

    if (this.selectedFeedbackStatus === this.feedback.feedback_status && (!this.commentText || !this.commentText.trim())) {
      this.toastr.warning('Please select a different status or add a comment.');
      return;
    }

    this.isSubmitting = true;

    const payload: any = {
      feedback_id: this.feedbackId || this.feedbackDetails?.conversation_feedback_id,
      wwid: Number(sessionStorage.getItem('userId')),
      current_status: this.feedback.feedback_status || '',
      new_status: this.selectedFeedbackStatus,
      user_type: sessionStorage.getItem('role') || '',
      activity_type: '',
      created_at: new Date().toISOString()
    };

    if (this.commentText && this.commentText.trim()) {
      payload.message = this.commentText.trim();
    } else {
      const previousStatus = this.feedback.feedback_status || 'Unknown';
      const newStatus = this.selectedFeedbackStatus;
      payload.message = `Status changed from ${previousStatus} to ${newStatus}`;
    }
    const file = this.uploadedFiles.length > 0 ? this.uploadedFiles[0] : null;
    this.feedbackService.addCommentWithStatus(file, payload).subscribe({
      next: (response: CommentResponse) => {
        this.toastr.success('Feedback updated successfully.');
        this.commentText = '';
        this.uploadedFiles = [];
        if (this.timelineRef) this.timelineRef.clearUploadedFiles();
        this.feedback.feedback_status = this.selectedFeedbackStatus;
        this.conversationDetails();
        this.isSubmitting = false;
      },
      error: (error: any) => {
        console.error('Error updating feedback:', error);
        let errorMessage = 'Failed to update feedback. Please try again.';
        if (error?.error?.detail) {
          const detail = error.error.detail;
          if (detail.includes('Invalid status transition')) {
            const allowedMatch = detail.match(/Allowed: \[(.*?)\]/);
            if (allowedMatch) {
              const allowedStatuses = allowedMatch[1]
                .split(',')
                .map(s => s.trim().replace(/'/g, ''))
                .join(', ');
              errorMessage = `Cannot change status from ${this.feedback.feedback_status} to ${this.selectedFeedbackStatus}. Allowed transitions: ${allowedStatuses}`;
            } else {
              errorMessage = detail;
            }
            this.selectedFeedbackStatus = this.feedback.feedback_status || '';
          } else {
            errorMessage = detail;
          }
        }

        this.toastr.error(errorMessage, '', { timeOut: 8000 });
        this.isSubmitting = false;
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/app-feedback'], { skipLocationChange: true });
  }

  ngOnDestroy(): void {
    if (this.feedbackSubscription) {
      this.feedbackSubscription.unsubscribe();
    }
  }

  openAssignTicketToDialog(): void {
    const dialogRef = this.dialog.open(AssignTicketToDialogComponent, {
      width: '500px',
      data: {
        ticketId: this.feedbackId,
        currentAssignee: this.feedback?.assigned_category || '',
        currentEta: this.feedback?.eta || '',
        currentDevTeam: this.feedback?.dev_team || '',
        referenceType: 'feedback'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.feedback.assigned_category = result.assigned_category;
        this.conversationDetails();
      }
    });
  }

}